import type { Category, MenuBoardConfig, Screen, ScoredItem, Section } from "../types.js";
import { chunk, groupBy } from "../utils.js";
import { countScreenItems } from "./rules.js";
import {
  createComboHighlightSection,
  createGridSection,
  createHeroSection,
  createListSection,
} from "./section-factory.js";

function buildScreen(index: number, sections: Section[]): Screen {
  return {
    index,
    sections,
    itemCount: countScreenItems(sections),
  };
}

export function buildHeroGridScreens(items: ScoredItem[], config: MenuBoardConfig): Screen[] {
  if (items.length === 0) return [];

  const screens: Screen[] = [];
  const remaining = [...items];
  let screenIndex = 0;

  // First screen: hero + grid
  const heroItem = remaining.shift()!;
  const sections: Section[] = [];

  if (heroItem.type === "combo") {
    sections.push(createComboHighlightSection([heroItem], 0));
  } else {
    sections.push(createHeroSection(heroItem));
  }

  const fillCount = config.maxItemsPerScreen - 1;
  const gridItems = remaining.splice(0, fillCount);

  if (gridItems.length > 0) {
    sections.push(createGridSection(gridItems, 2));
  }

  screens.push(buildScreen(screenIndex++, sections));

  // Subsequent screens: grid only
  const chunks = chunk(remaining, config.maxItemsPerScreen);
  for (const group of chunks) {
    if (config.maxScreens && screenIndex >= config.maxScreens) break;
    const combos = group.filter((i) => i.type === "combo");
    const products = group.filter((i) => i.type === "product");
    const screenSections: Section[] = [];
    let row = 0;

    if (combos.length > 0) {
      screenSections.push(createComboHighlightSection(combos, row));
      row += 2;
    }
    if (products.length > 0) {
      screenSections.push(createGridSection(products, row));
    }

    screens.push(buildScreen(screenIndex++, screenSections));
  }

  return screens;
}

export function buildGridOnlyScreens(items: ScoredItem[], config: MenuBoardConfig): Screen[] {
  if (items.length === 0) return [];

  const screens: Screen[] = [];
  const chunks = chunk(items, config.maxItemsPerScreen);

  for (const [i, group] of chunks.entries()) {
    if (config.maxScreens && i >= config.maxScreens) break;

    const half = Math.ceil(group.length / 2);
    const sections: Section[] = [];

    sections.push(createGridSection(group.slice(0, half), 0));
    if (group.length > half) {
      sections.push(createGridSection(group.slice(half), 1));
    }

    screens.push(buildScreen(i, sections));
  }

  return screens;
}

export function buildCategoryScreens(
  items: ScoredItem[],
  categories: Category[],
  config: MenuBoardConfig,
): Screen[] {
  if (items.length === 0) return [];

  const screens: Screen[] = [];
  let screenIndex = 0;

  const combos = items.filter((i) => i.type === "combo");
  const products = items.filter((i) => i.type === "product");

  // Combos get their own screen(s)
  if (combos.length > 0) {
    const comboChunks = chunk(combos, config.maxItemsPerScreen);
    for (const group of comboChunks) {
      if (config.maxScreens && screenIndex >= config.maxScreens) break;
      const sections: Section[] = [createComboHighlightSection(group.slice(0, 1), 0)];
      if (group.length > 1) {
        sections.push(createGridSection(group.slice(1), 2));
      }
      screens.push(buildScreen(screenIndex++, sections));
    }
  }

  // Group products by category
  const grouped = groupBy(products, (p) => p.categoryId ?? "uncategorized");
  const categoryOrder = categories.map((c) => c.id);

  for (const catId of categoryOrder) {
    if (config.maxScreens && screenIndex >= config.maxScreens) break;
    const catItems = grouped.get(catId);
    if (!catItems || catItems.length === 0) continue;

    const catChunks = chunk(catItems, config.maxItemsPerScreen);
    for (const group of catChunks) {
      if (config.maxScreens && screenIndex >= config.maxScreens) break;

      const sections: Section[] = [];
      let row = 0;

      // First item as hero if high score
      if (group.length >= 3) {
        sections.push(createHeroSection(group[0]));
        row = 2;
        const rest = group.slice(1);
        if (rest.length > 3) {
          sections.push(createGridSection(rest.slice(0, 3), row));
          sections.push(createListSection(rest.slice(3), row + 1));
        } else {
          sections.push(createGridSection(rest, row));
        }
      } else {
        sections.push(createGridSection(group, row));
      }

      screens.push(buildScreen(screenIndex++, sections));
    }
  }

  return screens;
}
